// /chats (the inbox) and /chats/:conversationId (a thread beside it on wide
// screens). `?with=<userId>` opens the DM with a member and `?circle=<id>`
// a circle's chat, and `?seq=<n>` on a thread jumps to that message (search
// results: components/ChatSearch). Message actions, settings and reports:
// components/ChatActions.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type UIEvent,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth, useMe } from "../auth/AuthContext";
import {
  BODY_COUNT_FROM,
  checkBody,
  conversationTitle,
  excerpt,
  inboxTime,
  insertMention,
  isCollapsed,
  isExpired,
  isListed,
  isMuted,
  MAX_BODY,
  mentionCandidates,
  mentionedIds,
  mentionQuery,
  mentionSegments,
  messageActions,
  muteText,
  timerLabel,
  needsTerms,
  newestSeq,
  personName,
  previewText,
  reactionAfterTap,
  seenText,
  summaryText,
  systemText,
  threadItems,
  typingText,
  typingUsers,
  unsentText,
  type ChatUser,
  type Conversation,
  type Message,
  type MessageActions,
  type PendingSend,
  type ThreadItem,
  type Users,
} from "../chat/chat";
import { useChat } from "../chat/ChatContext";
import type { ChatSnapshot, ChatStore, ThreadState } from "../chat/store";
import { useBlocks, useCapabilities, useSetBlocked } from "../chat/useChatAccount";
import type { MediaLimits } from "../chat/media";
import { trayReady, type AttachmentTray, type TraySnapshot } from "../chat/tray";
import { ConversationSettings, MessageMenu, ReportDialog, type ReportTarget } from "../components/ChatActions";
import { ChatSearch } from "../components/ChatSearch";
import { AttachButton, MessageMedia, TrayView, useAttachmentTray } from "../components/ChatMedia";
import {
  EtaStrip,
  LocationCard,
  PinnedBanner,
  ShareLocationButton,
  ShareLocationSheet,
} from "../components/ChatLocation";
import { ownLiveEta, pendingLocationText } from "../chat/location";
import { voiceRecorderType } from "../chat/recorder";
import { VOICE_MAX_MS } from "../chat/voice";
import {
  MessageVoice,
  MicButton,
  VoiceBar,
  VoiceNotice,
  VoicePlayer,
  useVoiceRecorder,
  type VoiceSetup,
} from "../components/ChatVoice";
import { timeText } from "../history/history";
import { MemberAvatar } from "../components/MemberAvatar";
import { useNow } from "../useNow";

const TICK_MS = 60_000;
/** Within this distance of the bottom the thread counts as showing its newest messages. */
const BOTTOM_SLACK_PX = 48;
/** Older messages are paged in once the top is this close. */
const OLDER_AT_PX = 400;

export function ChatsPage() {
  const { conversationId } = useParams();
  const { session } = useAuth();
  const { store, chat } = useChat();
  const nowMs = useNow(TICK_MS);
  if (!session || !store || !chat) return null;
  return (
    <div className={`chats-page${conversationId ? " has-thread" : ""}`}>
      <Inbox chat={chat} selectedId={conversationId} selfId={session.userId} nowMs={nowMs} />
      {conversationId ? (
        <ThreadView
          key={conversationId}
          id={conversationId}
          store={store}
          chat={chat}
          selfId={session.userId}
          nowMs={nowMs}
        />
      ) : (
        <ChatLauncher store={store} chat={chat} />
      )}
    </div>
  );
}

/** The empty thread pane; resolves `?with=` and `?circle=` to a conversation and opens it. */
function ChatLauncher({ store, chat }: { store: ChatStore; chat: ChatSnapshot }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const withUser = params.get("with");
  const circleId = params.get("circle");
  const [failed, setFailed] = useState<{ key: string; message: string } | null>(null);

  useEffect(() => {
    if (!withUser) return;
    let cancelled = false;
    store.direct(withUser).then(
      (id) => {
        if (!cancelled) void navigate(`/chats/${id}`, { replace: true });
      },
      (error: unknown) => {
        if (cancelled) return;
        setFailed({
          key: `with:${withUser}`,
          message: errorMessage(error, { 403: "You can only message people you share a circle with." }),
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [store, withUser, navigate]);

  const circleChat = circleId ? chat.conversations.find((c) => c.circle_id === circleId) : undefined;
  useEffect(() => {
    if (circleChat) void navigate(`/chats/${circleChat.id}`, { replace: true });
  }, [circleChat, navigate]);

  let text = "Pick a chat.";
  if (withUser) text = failed?.key === `with:${withUser}` ? failed.message : "Opening the chat…";
  else if (circleId) text = chat.inboxLoaded ? "This circle's chat isn't available." : "Opening the chat…";
  return (
    <section className={`chat-thread chat-empty${withUser || circleId ? " launching" : ""}`} aria-live="polite">
      <p className="muted">{text}</p>
    </section>
  );
}

// ------------------------------------------------------------------ inbox

function Inbox(props: { chat: ChatSnapshot; selectedId: string | undefined; selfId: string; nowMs: number }) {
  const { chat, selectedId, selfId, nowMs } = props;
  const listed = chat.conversations.filter(isListed);
  return (
    <nav className="chat-inbox" aria-label="Chats">
      <div className="chat-inbox-header">
        <h1>Chats</h1>
        {chat.status === "reconnecting" && <span className="hint">Reconnecting…</span>}
      </div>
      <ChatSearch selfId={selfId} nowMs={nowMs} users={chat.users}>
        {!chat.inboxLoaded && !chat.inboxError && <p className="muted chat-inbox-note">Loading chats…</p>}
        {chat.inboxError && <p className="error chat-inbox-note">{chat.inboxError}</p>}
        {chat.inboxLoaded && listed.length === 0 && (
          <p className="muted chat-inbox-note">
            No chats yet. Every circle has one, and you can message anyone you share a circle with.
          </p>
        )}
        <ul className="chat-inbox-list">
          {listed.map((conversation) => (
            <InboxRow
              key={conversation.id}
              conversation={conversation}
              users={chat.users}
              selected={conversation.id === selectedId}
              selfId={selfId}
              nowMs={nowMs}
            />
          ))}
        </ul>
      </ChatSearch>
    </nav>
  );
}

function ConversationAvatar({ conversation }: { conversation: Conversation }) {
  return (
    <MemberAvatar
      userId={conversation.peer_id ?? conversation.circle_id ?? conversation.id}
      name={conversationTitle(conversation)}
      avatarUrl={conversation.avatar_small_url ?? null}
      isSelf={false}
    />
  );
}

function InboxRow(props: {
  conversation: Conversation;
  users: Users;
  selected: boolean;
  selfId: string;
  nowMs: number;
}) {
  const { conversation, users, selected, selfId, nowMs } = props;
  const muted = isMuted(conversation, nowMs) || conversation.notify === "none";
  const unread = conversation.unread_count;
  const title = conversationTitle(conversation);
  return (
    <li>
      <Link
        to={`/chats/${conversation.id}`}
        className={`chat-inbox-item${selected ? " selected" : ""}${unread > 0 ? " unread" : ""}`}
        aria-current={selected ? "page" : undefined}
      >
        <ConversationAvatar conversation={conversation} />
        <span className="chat-inbox-text">
          <span className="chat-inbox-top">
            <span className="chat-inbox-title">{title}</span>
            {conversation.kind === "event" && (
              <span className={`event-badge${conversation.writable ? "" : " ended"}`}>
                {conversation.writable ? "Event" : "Closed"}
              </span>
            )}
            <time className="chat-inbox-time" dateTime={conversation.last_activity_at}>
              {inboxTime(conversation.last_activity_at, nowMs)}
            </time>
          </span>
          <span className="chat-inbox-bottom">
            <span className="chat-inbox-preview">{previewText(conversation, users, selfId, nowMs)}</span>
            {muted && (
              <span className="chat-muted" title="Muted" aria-label="Muted">
                🔕
              </span>
            )}
            {unread > 0 && (
              <span
                className={`chat-badge${muted ? " muted" : ""}`}
                aria-label={`${unread} unread${conversation.mention_count > 0 ? ", mentioned" : ""}`}
              >
                {conversation.mention_count > 0 && "@ "}
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

// ----------------------------------------------------------------- thread

function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const change = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", change);
    return () => document.removeEventListener("visibilitychange", change);
  }, []);
  return visible;
}

/** A reply being written, or an own message being edited (with its new text). */
/** Composer text and the people picked from its mention suggestions (which tells apart people with one name). */
interface ComposerValue {
  text: string;
  picked: ReadonlySet<string>;
}

/**
 * A reply being written (in the ordinary composer text), or an own message
 * being edited (with its own text; `token` tells one edit session from the next).
 */
type Draft =
  { kind: "reply"; message: Message } | { kind: "edit"; message: Message; value: ComposerValue; token: number } | null;

/** Scrolls a message into view (and flashes it) if it is loaded. */
function showMessage(seq: number, behavior: ScrollBehavior = "smooth") {
  const row = document.querySelector<HTMLElement>(`.chat-messages [data-seq="${seq}"]`);
  if (!row) return;
  row.scrollIntoView({ block: "center", behavior });
  row.classList.add("flash");
  setTimeout(() => row.classList.remove("flash"), 1_500);
}

/** What a message row can ask the thread to do. */
interface RowHandlers {
  react: (message: Message, emoji: string) => void;
  reply: (message: Message) => void;
  edit: (message: Message) => void;
  unsend: (message: Message) => void;
  report: (message: Message) => void;
  block: (message: Message) => void;
  pin: (message: Message) => void;
  unpin: () => void;
  /** Stop your own live ETA. */
  stop: (message: Message) => void;
}

/** The text for a failed message action. */
function actionErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "edit_window_closed":
        return "Messages can only be edited for 15 minutes after sending.";
      case "message_unsent":
        return "That message was unsent.";
      case "not_writable":
        return "You can't change messages in this chat any more.";
      case "terms_required":
        return "Agree to the chat terms first.";
    }
  }
  return errorMessage(error);
}

/** Whether the caller is an admin of the circle (whose admins may remove any message in its chat). */
function useCircleAdmin(circleId: string | null, selfId: string): boolean {
  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => {
      if (!circleId) return [];
      return unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } }));
    },
    enabled: circleId !== null,
  });
  return members.data?.find((m) => m.user_id === selfId)?.role === "admin";
}

function ThreadView(props: { id: string; store: ChatStore; chat: ChatSnapshot; selfId: string; nowMs: number }) {
  const { id, store, chat, selfId, nowMs } = props;
  useEffect(() => store.open(id), [store, id]);
  const thread = chat.threads[id];
  const conversation = chat.conversations.find((c) => c.id === id);
  const me = useMe();
  const capabilities = useCapabilities();
  const { ids: blocked } = useBlocks();
  const setBlocked = useSetBlocked();
  const admin = useCircleAdmin(conversation?.circle_id ?? null, selfId);
  const [compose, setCompose] = useState<ComposerValue>(() => ({ text: "", picked: new Set() }));
  const [draftState, setDraft] = useState<Draft>(null);
  // A reply to (or edit of) a message that disappeared ends with it, text and all.
  const draft = draftState && isExpired(draftState.message, nowMs) ? null : draftState;
  const editTokens = useRef(0);
  const [report, setReport] = useState<{ target: ReportTarget; blockId: string | null } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const attachments = useAttachmentTray(id);
  const [dropping, setDropping] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [recorderType] = useState(voiceRecorderType);

  // `?seq=` (a search result): load back to that message, then show it once
  // its row is rendered (the conversation may still be loading). Nothing is
  // marked read meanwhile.
  const [params, setParams] = useSearchParams();
  const jumpSeq = /^\d{1,15}$/.test(params.get("seq") ?? "") ? Number(params.get("seq")) : null;
  const [showSeq, setShowSeq] = useState<number | null>(null);
  useEffect(() => {
    if (jumpSeq === null) return;
    let cancelled = false;
    void store.loadThrough(id, jumpSeq).then((found) => {
      if (cancelled) return;
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("seq");
          return next;
        },
        { replace: true },
      );
      if (found) setShowSeq(jumpSeq);
      else setActionError("That message isn't in this chat any more.");
    });
    return () => {
      cancelled = true;
    };
  }, [store, id, jumpSeq, setParams]);
  useEffect(() => {
    if (showSeq === null) return;
    const frame = requestAnimationFrame(() => {
      if (document.querySelector(`.chat-messages [data-seq="${showSeq}"]`)) showMessage(showSeq, "auto");
      // Gone again meanwhile (unsent, cleared): stop waiting.
      else if (thread?.messages.some((message) => message.seq === showSeq)) return;
      setShowSeq(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [showSeq, thread, conversation]);

  if (thread?.lost) {
    return (
      <section className="chat-thread chat-empty">
        <p className="muted">This chat isn't available: you may have left the circle, or it was deleted.</p>
        <p>
          <Link to="/chats">All chats</Link>
        </p>
      </section>
    );
  }
  if (!thread || !conversation) {
    return (
      <section className="chat-thread chat-empty">
        {thread?.error ? <p className="error">{thread.error}</p> : <p className="muted">Loading the chat…</p>}
      </section>
    );
  }

  const users = chat.users;
  const direct = conversation.kind === "direct";
  const peer =
    direct && conversation.peer_id
      ? { id: conversation.peer_id, name: conversationTitle(conversation), blocked: blocked.has(conversation.peer_id) }
      : null;

  function act(action: () => Promise<unknown>) {
    setActionError(null);
    action().catch((error: unknown) => setActionError(actionErrorText(error)));
  }

  function blockPerson(userId: string) {
    const name = personName(users, userId);
    const question =
      `Block ${name}? Their messages are hidden behind "Show", you won't be notified about them and ` +
      "neither of you can write in your direct chat. Circles you share, and location sharing, don't change.";
    if (window.confirm(question)) act(() => setBlocked.mutateAsync({ userId, blocked: true }));
  }

  const handlers: RowHandlers = {
    react: (message, emoji) => act(() => store.react(id, message.id, reactionAfterTap(message, selfId, emoji))),
    reply: (message) => setDraft({ kind: "reply", message }),
    edit: (message) =>
      setDraft({
        kind: "edit",
        message,
        value: { text: message.body ?? "", picked: new Set(message.mentions) },
        token: ++editTokens.current,
      }),
    unsend: (message) => {
      const own = message.sender_id === selfId;
      const question = own
        ? "Unsend this message? It's removed for everyone."
        : `Remove this message from ${personName(users, message.sender_id)} for everyone?`;
      if (!window.confirm(question)) return;
      if (draft?.message.id === message.id) setDraft(null);
      act(() => store.unsend(id, message.id));
    },
    report: (message) =>
      setReport({
        target: { kind: "message", messageId: message.id, name: personName(users, message.sender_id) },
        blockId: message.sender_id && !blocked.has(message.sender_id) ? message.sender_id : null,
      }),
    block: (message) => {
      if (message.sender_id) blockPerson(message.sender_id);
    },
    pin: (message) => act(() => store.pin(id, message.id)),
    unpin: () => {
      if (window.confirm("Unpin this message for everyone?")) act(() => store.unpin(id));
    },
    stop: (message) => act(() => store.stopEta(id, message.id)),
  };

  const held = thread.pending.filter((p) => p.status === "failed" && p.code === "terms_required");
  const termsVersion = capabilities.data?.terms_version;
  const termsNeeded = me.data !== undefined && needsTerms(termsVersion, me.data.terms_accepted_version);
  const typers = typingUsers(chat.typing, id, nowMs).filter((userId) => !blocked.has(userId));
  // Photos, videos and files: when the server stores them and this user may write here.
  const limits = capabilities.data?.media;
  const tray = attachments.tray;
  const canAttach = limits !== undefined && conversation.writable && !termsNeeded && held.length === 0;
  const attach = canAttach ? (files: File[]) => tray.add(files, limits) : null;
  const withFiles = (event: DragEvent) => attach !== null && event.dataTransfer.types.includes("Files");
  const canShare = conversation.writable && !termsNeeded && held.length === 0;
  const voice: VoiceSetup | null =
    canShare && recorderType && limits?.audio_max_bytes !== undefined
      ? {
          type: recorderType,
          maxMs: limits.audio_max_duration_ms ?? VOICE_MAX_MS,
          maxBytes: limits.audio_max_bytes,
        }
      : null;
  const liveEta = ownLiveEta(thread.messages, selfId);

  return (
    <section
      className={`chat-thread${dropping ? " dropping" : ""}`}
      aria-label={conversationTitle(conversation)}
      onDragOver={(event) => {
        if (!withFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDropping(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(event) => {
        setDropping(false);
        if (!withFiles(event)) return;
        event.preventDefault();
        attach?.([...event.dataTransfer.files]);
      }}
    >
      {dropping && (
        <div className="chat-drop" aria-hidden="true">
          Drop to attach
        </div>
      )}
      <ThreadHeader
        conversation={conversation}
        thread={thread}
        nowMs={nowMs}
        settingsOpen={settingsOpen}
        onSettings={() => setSettingsOpen((open) => !open)}
        searching={searching}
        onSearch={() => setSearching((open) => !open)}
      />
      {searching && (
        <div className="chat-thread-search">
          <ChatSearch
            scope={conversation}
            selfId={selfId}
            nowMs={nowMs}
            users={users}
            autoFocus
            onPick={() => setSearching(false)}
            onClose={() => setSearching(false)}
          />
        </div>
      )}
      {settingsOpen && (
        <ConversationSettings
          conversation={conversation}
          store={store}
          nowMs={nowMs}
          admin={admin}
          peer={peer}
          onReportPeer={() => {
            if (!peer) return;
            setSettingsOpen(false);
            setReport({ target: { kind: "user", userId: peer.id, name: peer.name }, blockId: null });
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      <PinnedBanner
        conversation={conversation}
        store={store}
        selfId={selfId}
        nowMs={nowMs}
        liveEta={liveEta}
        onShow={showMessage}
        onError={(error) => setActionError(actionErrorText(error))}
      />
      <MessageList
        id={id}
        store={store}
        thread={thread}
        conversation={conversation}
        users={users}
        selfId={selfId}
        nowMs={nowMs}
        admin={admin && !direct}
        blocked={blocked}
        handlers={handlers}
        jumping={jumpSeq !== null || showSeq !== null}
      />
      <p className="chat-typing hint" aria-live="polite">
        {typingText(typers, users, direct)}
      </p>
      <EtaStrip messages={thread.messages} selfId={selfId} onStop={handlers.stop} />
      {actionError && (
        <p className="chat-action-error error" role="alert">
          {actionError}{" "}
          <button type="button" className="link-button" onClick={() => setActionError(null)}>
            Dismiss
          </button>
        </p>
      )}
      {!conversation.writable ? (
        <p className="chat-readonly" role="status">
          {peer?.blocked
            ? `You blocked ${peer.name}. Unblock them in this chat's settings to write again.`
            : conversation.kind === "event"
              ? "This event's chat is closed. It is deleted with the event 30 days after the end."
              : "This chat is read-only. You can message people while you share a circle with them."}
        </p>
      ) : termsNeeded || held.length > 0 ? (
        <TermsNotice store={store} id={id} held={held} termsVersion={termsVersion} />
      ) : (
        <>
          {sharing && canShare && (
            <ShareLocationSheet
              conversation={conversation}
              store={store}
              selfId={selfId}
              nowMs={nowMs}
              liveEta={liveEta}
              replyToId={draft?.kind === "reply" ? draft.message.id : null}
              onSent={() => setDraft((current) => (current?.kind === "reply" ? null : current))}
              onClose={() => setSharing(false)}
            />
          )}
          <Composer
            store={store}
            id={id}
            selfId={selfId}
            users={users}
            participants={thread.participants ?? []}
            value={draft?.kind === "edit" ? draft.value : compose}
            onValue={(value) => (draft?.kind === "edit" ? setDraft({ ...draft, value }) : setCompose(value))}
            draft={draft}
            onDraftDone={() => setDraft(null)}
            onEditSaved={(token) =>
              // Only the edit session that was saved: another draft may have started since.
              setDraft((current) => (current?.kind === "edit" && current.token === token ? null : current))
            }
            onSent={() => setCompose({ text: "", picked: new Set() })}
            onError={(error) => setActionError(actionErrorText(error))}
            typing={me.data?.chat_receipts !== false}
            tray={canAttach ? { tray, snapshot: attachments.snapshot, limits } : null}
            share={canShare ? { open: sharing, toggle: () => setSharing((open) => !open) } : null}
            voice={voice}
          />
        </>
      )}
      {report && <ReportDialog target={report.target} blockId={report.blockId} onClose={() => setReport(null)} />}
    </section>
  );
}

function ThreadHeader(props: {
  conversation: Conversation;
  thread: ThreadState;
  nowMs: number;
  settingsOpen: boolean;
  onSettings: () => void;
  searching: boolean;
  onSearch: () => void;
}) {
  const { conversation, thread, nowMs } = props;
  const members = thread.participants?.length;
  const muted = muteText(conversation, nowMs);
  const timer = conversation.message_timer ? timerLabel(conversation.message_timer) : null;
  return (
    <header className="chat-thread-header">
      <Link to="/chats" className="chat-back" aria-label="All chats">
        ←
      </Link>
      <ConversationAvatar conversation={conversation} />
      <span className="chat-thread-title">
        <h2>
          {conversationTitle(conversation)}
          {conversation.kind === "event" && <span className="event-badge">Event</span>}
          {timer && (
            <span className="timer-badge" title={`New messages disappear after ${timer}`}>
              ⏱ {timer}
            </span>
          )}
        </h2>
        {(conversation.kind !== "direct" && members !== undefined) || muted ? (
          <span className="hint">
            {conversation.kind !== "direct" &&
              members !== undefined &&
              `${members} ${members === 1 ? "member" : "members"}`}
            {conversation.kind !== "direct" && members !== undefined && muted && " · "}
            {muted}
          </span>
        ) : null}
      </span>
      {conversation.circle_id && (
        <Link to={`/circles/${conversation.circle_id}`} className="chat-thread-link">
          {conversation.kind === "event" ? "Event" : "Circle"}
        </Link>
      )}
      <button
        type="button"
        className="chat-icon-button"
        aria-label="Search in this chat"
        aria-pressed={props.searching}
        onClick={props.onSearch}
      >
        🔍
      </button>
      <button
        type="button"
        className="chat-icon-button"
        aria-label="Chat settings"
        data-popover-toggle
        aria-expanded={props.settingsOpen}
        onClick={props.onSettings}
      >
        ⋯
      </button>
    </header>
  );
}

function MessageList(props: {
  id: string;
  store: ChatStore;
  thread: ThreadState;
  conversation: Conversation;
  users: Users;
  selfId: string;
  nowMs: number;
  admin: boolean;
  blocked: ReadonlySet<string>;
  handlers: RowHandlers;
  /** Jumping to a search result: read nothing until it is shown. */
  jumping: boolean;
}) {
  const { id, store, thread, conversation, users, selfId, nowMs, jumping } = props;
  const items = useMemo(() => threadItems(thread, selfId, nowMs), [thread, selfId, nowMs]);
  const listRef = useRef<HTMLDivElement>(null);
  /** Keep the newest message in view as more arrive (until the user scrolls up). */
  const stick = useRef(true);
  const layout = useRef({ firstSeq: 0, height: 0 });
  const [atBottom, setAtBottom] = useState(true);
  const visible = usePageVisible();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  /** Blocked people's messages the user chose to show. */
  const [shown, setShown] = useState<ReadonlySet<string>>(() => new Set());

  // Stay at the bottom, or keep the reading position when older messages are prepended.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const firstSeq = thread.messages[0]?.seq ?? 0;
    if (stick.current) el.scrollTop = el.scrollHeight;
    else if (firstSeq < layout.current.firstSeq) el.scrollTop += el.scrollHeight - layout.current.height;
    layout.current = { firstSeq, height: el.scrollHeight };
  }, [items, thread.messages]);

  // Sending scrolls to your message.
  const pendingCount = thread.pending.length;
  useLayoutEffect(() => {
    if (pendingCount === 0) return;
    stick.current = true;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [pendingCount]);

  // Read only what is on screen: the page is visible and the newest messages
  // are in view (measured, since a jump scrolls before any scroll event).
  const newest = newestSeq(thread);
  useEffect(() => {
    const el = listRef.current;
    const bottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK_PX : atBottom;
    if (visible && !jumping && bottom && thread.loaded && newest > conversation.read_seq) store.markRead(id, newest);
  }, [visible, jumping, atBottom, thread.loaded, newest, conversation.read_seq, store, id]);

  // A first page shorter than the pane has no scrollbar to reach the top with.
  useEffect(() => {
    const el = listRef.current;
    if (el && thread.hasOlder && el.scrollHeight <= el.clientHeight) void store.loadOlder(id);
  }, [thread.hasOlder, thread.messages, store, id]);

  function onScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK_PX;
    stick.current = bottom;
    setAtBottom(bottom);
    if (el.scrollTop < OLDER_AT_PX && thread.hasOlder) void store.loadOlder(id);
  }

  const direct = conversation.kind === "direct";
  const context = {
    writable: conversation.writable,
    admin: props.admin,
    nowMs,
    pinnedId: conversation.pinned?.message.id ?? null,
  };
  return (
    <div className="chat-messages" ref={listRef} onScroll={onScroll}>
      {thread.loaded && !thread.hasOlder && <p className="chat-start hint">The start of the conversation</p>}
      {thread.loadingOlder && <p className="chat-start hint">Loading earlier messages…</p>}
      {!thread.loaded && !thread.error && <p className="chat-start muted">Loading messages…</p>}
      {thread.error && <p className="chat-start error">{thread.error}</p>}
      <ol className="chat-items">
        {items.map((item) => {
          switch (item.kind) {
            case "day":
              return (
                <li key={item.key} className="chat-day">
                  <span>{item.title}</span>
                </li>
              );
            case "system":
              return (
                <li key={item.key} className="chat-system">
                  {systemText(item.message, users, selfId)}
                </li>
              );
            case "message":
              return (
                <MessageRow
                  key={item.key}
                  item={item}
                  users={users}
                  selfId={selfId}
                  direct={direct}
                  actions={messageActions(item.message, selfId, context)}
                  collapsed={isCollapsed(item.message, props.blocked, shown)}
                  onShow={() => setShown((set) => new Set(set).add(item.message.id))}
                  menuOpen={menuFor === item.message.id}
                  onMenu={(open) => setMenuFor(open ? item.message.id : null)}
                  onQuote={showMessage}
                  onExpired={() => store.resign(id, item.message.seq)}
                  handlers={props.handlers}
                  nowMs={nowMs}
                />
              );
            case "pending":
              return <PendingRow key={item.key} pending={item.pending} first={item.first} store={store} id={id} />;
          }
        })}
      </ol>
    </div>
  );
}

function MessageRow(props: {
  item: Extract<ThreadItem, { kind: "message" }>;
  users: Users;
  selfId: string;
  direct: boolean;
  actions: MessageActions;
  collapsed: boolean;
  onShow: () => void;
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  onQuote: (seq: number) => void;
  /** A media URL ran out: fetch the message again. */
  onExpired: () => void;
  handlers: RowHandlers;
  nowMs: number;
}) {
  const { item, users, selfId, direct, actions, collapsed, handlers } = props;
  const message = item.message;
  const name = personName(users, message.sender_id);
  const showSender = !item.own && !direct && item.first;
  const created = Date.parse(message.created_at);
  const hasMenu =
    !collapsed &&
    message.deleted_at === null &&
    (message.kind === "text" || message.kind === "media" || message.kind === "location" || message.kind === "voice");
  const close = () => props.onMenu(false);

  let bubble;
  if (collapsed) {
    bubble = (
      <div className="chat-bubble unsent">
        <span className="chat-unsent">Message from someone you blocked</span>{" "}
        <button type="button" className="link-button" onClick={props.onShow}>
          Show
        </button>
      </div>
    );
  } else {
    bubble = (
      <div
        className={`chat-bubble${message.deleted_at ? " unsent" : ""}${(message.kind === "media" || message.kind === "location") && !message.deleted_at ? " media" : ""}${message.kind === "voice" && !message.deleted_at ? " voice" : ""}`}
        title={new Date(created).toLocaleString()}
      >
        {message.reply_to_id && !message.deleted_at && (
          <Quote message={message} users={users} selfId={selfId} nowMs={props.nowMs} onQuote={props.onQuote} />
        )}
        {message.kind === "media" && !message.deleted_at && (
          <MessageMedia attachments={message.attachments ?? []} onExpired={props.onExpired} />
        )}
        {message.kind === "location" && !message.deleted_at && (
          <LocationCard message={message} selfId={selfId} nowMs={props.nowMs} onStop={() => handlers.stop(message)} />
        )}
        {message.kind === "voice" && !message.deleted_at && (
          <MessageVoice message={message} onExpired={props.onExpired} />
        )}
        {message.deleted_at ? (
          <span className="chat-unsent">{unsentText(message, users, selfId)}</span>
        ) : message.body === null ? null : (
          <span className="chat-text">
            {mentionSegments(message, users).map((segment, i) =>
              segment.mention ? (
                <span key={i} className={`chat-mention${segment.mention === selfId ? " me" : ""}`}>
                  {segment.text}
                </span>
              ) : (
                <Fragment key={i}>{segment.text}</Fragment>
              ),
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <li
      className={`chat-message${item.own ? " own" : ""}${item.first ? " first" : ""}${item.last ? " last" : ""}`}
      data-seq={message.seq}
    >
      {!item.own && !direct && (
        <span className="chat-message-avatar">
          {item.first && (
            <MemberAvatar
              userId={message.sender_id ?? message.id}
              name={name}
              avatarUrl={(message.sender_id && users[message.sender_id]?.avatar_small_url) || null}
              isSelf={false}
            />
          )}
        </span>
      )}
      <div className="chat-message-body">
        {showSender && <span className="chat-sender">{name}</span>}
        <div className={`chat-bubble-row${props.menuOpen ? " open" : ""}`}>
          {bubble}
          {hasMenu && (
            <button
              type="button"
              className="chat-more"
              aria-label="Message actions"
              data-popover-toggle
              aria-haspopup="menu"
              aria-expanded={props.menuOpen}
              onClick={() => props.onMenu(!props.menuOpen)}
            >
              ⋯
            </button>
          )}
          {hasMenu && props.menuOpen && (
            <MessageMenu
              message={message}
              selfId={selfId}
              senderName={name}
              actions={actions}
              handlers={{
                onReact: (emoji) => {
                  close();
                  handlers.react(message, emoji);
                },
                onReply: () => {
                  close();
                  handlers.reply(message);
                },
                onEdit: () => {
                  close();
                  handlers.edit(message);
                },
                onUnsend: () => {
                  close();
                  handlers.unsend(message);
                },
                onReport: () => {
                  close();
                  handlers.report(message);
                },
                onBlock: () => {
                  close();
                  handlers.block(message);
                },
                onPin: () => {
                  close();
                  handlers.pin(message);
                },
                onUnpin: () => {
                  close();
                  handlers.unpin();
                },
                onClose: close,
              }}
            />
          )}
        </div>
        {!collapsed && message.reactions.length > 0 && (
          <span className="chat-reactions">
            {message.reactions.map((reaction) => {
              const mine = reaction.user_ids.includes(selfId);
              const names = reaction.user_ids.map((userId) => personName(users, userId)).join(", ");
              return (
                <button
                  type="button"
                  key={reaction.emoji}
                  className={`chat-reaction${mine ? " mine" : ""}`}
                  title={names}
                  aria-label={`${reaction.emoji} from ${names}${mine ? " (take back)" : ""}`}
                  aria-pressed={mine}
                  disabled={!actions.react && !mine}
                  onClick={() => handlers.react(message, reaction.emoji)}
                >
                  {reaction.emoji}
                  {reaction.user_ids.length > 1 && ` ${reaction.user_ids.length}`}
                </button>
              );
            })}
          </span>
        )}
        {(item.last || message.edited_at) && (
          <span className="chat-meta">
            {item.last && <time dateTime={message.created_at}>{timeText(created)}</time>}
            {message.edited_at && !message.deleted_at && <span>edited</span>}
          </span>
        )}
        {item.seenBy.length > 0 && <span className="chat-seen">{seenText(item.seenBy, users, direct)}</span>}
      </div>
    </li>
  );
}

function Quote(props: {
  message: Message;
  users: Users;
  selfId: string;
  nowMs: number;
  onQuote: (seq: number) => void;
}) {
  const { message, users, selfId, nowMs } = props;
  const quote = message.reply_to;
  if (!quote) return <span className="chat-quote muted">Earlier message</span>;
  // The quoted message disappeared: its text goes with it, before the sweep's update arrives.
  if (isExpired(quote, nowMs)) return <span className="chat-quote muted">Message disappeared</span>;
  const who = quote.sender_id === selfId ? "You" : personName(users, quote.sender_id);
  return (
    <button type="button" className="chat-quote" onClick={() => props.onQuote(quote.seq)}>
      <span className="chat-quote-sender">{who}</span>
      <span className="chat-quote-text">
        {quote.deleted_at
          ? "Message unsent"
          : (quote.body ??
            (quote.kind === "media"
              ? "Attachment"
              : quote.kind === "location"
                ? "Location"
                : quote.kind === "voice"
                  ? "Voice message"
                  : ""))}
      </span>
    </button>
  );
}

function PendingRow(props: { pending: PendingSend; first: boolean; store: ChatStore; id: string }) {
  const { pending, store, id } = props;
  const failed = pending.status === "failed";
  return (
    <li className={`chat-message own pending${failed ? " failed" : ""}${props.first ? " first" : ""} last`}>
      <div className="chat-message-body">
        <div className={`chat-bubble${pending.voice ? " voice" : pending.attachments?.length ? " media" : ""}`}>
          {pending.voice && pending.attachments?.[0] ? (
            <VoicePlayer media={pending.attachments[0]} clip={pending.voice} />
          ) : pending.attachments?.length ? (
            <MessageMedia attachments={pending.attachments} />
          ) : null}
          {pending.location && <span className="chat-text">📍 {pendingLocationText(pending.location)}</span>}
          {pending.body && <span className="chat-text">{pending.body}</span>}
        </div>
        {failed ? (
          <span className="chat-meta error" role="alert">
            Not sent{pending.error && pending.code !== "terms_required" ? `: ${pending.error}` : ""}
            {pending.code !== "terms_required" && pending.code !== "not_writable" && (
              <button type="button" className="link-button" onClick={() => store.retry(id, pending.client_id)}>
                Retry
              </button>
            )}
            <button type="button" className="link-button" onClick={() => store.discard(id, pending.client_id)}>
              Discard
            </button>
          </span>
        ) : (
          <span className="chat-meta">Sending…</span>
        )}
      </div>
    </li>
  );
}

/**
 * Sending needs the current acceptable-use terms: shown in place of the
 * composer before the first message, or when a send was refused for them
 * (then agreeing sends what they held back).
 */
function TermsNotice(props: { store: ChatStore; id: string; held: PendingSend[]; termsVersion: number | undefined }) {
  const { store, id, held } = props;
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function agree() {
    setBusy(true);
    setError(null);
    try {
      // A refused send means the version may have changed since capabilities were read.
      const version =
        held.length > 0 || props.termsVersion === undefined
          ? unwrap(await api.GET("/capabilities")).terms_version
          : props.termsVersion;
      if (version === undefined) throw new Error("This server doesn't offer chat.");
      const me = unwrap(await api.PATCH("/me", { body: { terms_accepted_version: version } }));
      queryClient.setQueryData(["me"], me);
      for (const p of held) store.retry(id, p.client_id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat-terms card" role={held.length > 0 ? "alert" : "region"} aria-label="Chat terms">
      <p>
        Before your first message, agree to keep chats respectful: no harassment, threats, spam or illegal content.
        People can report messages and block each other, and the operator of this server can remove messages and suspend
        accounts.{" "}
        <a href="/terms" target="_blank" rel="noopener">
          Read the chat terms
        </a>
      </p>
      {error && <p className="error">{error}</p>}
      <div className="button-row">
        <button type="button" onClick={() => void agree()} disabled={busy}>
          {busy ? "Saving…" : held.length > 0 ? "Agree and send" : "Agree"}
        </button>
      </div>
    </div>
  );
}

function Composer(props: {
  store: ChatStore;
  id: string;
  selfId: string;
  users: Users;
  participants: readonly ChatUser[];
  value: ComposerValue;
  onValue: (value: ComposerValue) => void;
  draft: Draft;
  onDraftDone: () => void;
  /** An edit was saved; `token` names its session. */
  onEditSaved: (token: number) => void;
  onSent: () => void;
  onError: (error: unknown) => void;
  /** Tell others when this user types (their read receipts & typing switch). */
  typing: boolean;
  /** Attachments, when this user may send them here. */
  tray: { tray: AttachmentTray; snapshot: TraySnapshot; limits: MediaLimits } | null;
  /** The "Share location" sheet's toggle, when this user may send one here. */
  share: { open: boolean; toggle: () => void } | null;
  /** Voice messages, when this browser records them and this user may send them here. */
  voice: VoiceSetup | null;
}) {
  const { store, id, selfId, users, participants, draft } = props;
  const { text, picked } = props.value;
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  /** The `@` whose suggestions were dismissed with Escape. */
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const editing = draft?.kind === "edit" ? draft.message : null;
  const check = checkBody(text);
  const left = MAX_BODY - check.length;
  const attached = !editing && props.tray !== null ? props.tray.snapshot.items : [];
  // With attachments the caption is optional, but they must all be uploaded.
  const canSend = attached.length > 0 ? trayReady(attached) && (check.length === 0 || check.ok) : check.ok;
  const draftNow = useRef(draft);
  useLayoutEffect(() => {
    draftNow.current = draft;
  });
  const recorder = useVoiceRecorder(id, props.voice, (media, clip) => {
    // A reply started before recording quotes; the reply is done once sent.
    const current = draftNow.current;
    store.sendVoice(id, media, clip, { replyToId: current?.kind === "reply" ? current.message.id : null });
    if (current?.kind === "reply") props.onDraftDone();
  });
  const voiceBusy = recorder.state.phase !== "idle";
  // The mic stands in for Send while there is nothing to send; a recording
  // or upload under way keeps it (an edit started meanwhile waits).
  const showMic = props.voice !== null && (voiceBusy || (!editing && text.trim() === "" && attached.length === 0));

  const query = mentionQuery(text, caret);
  const candidates = query && query.start !== dismissed ? mentionCandidates(participants, query.query, selfId) : [];
  const suggesting = candidates.length > 0;
  const activeIndex = Math.min(active, Math.max(0, candidates.length - 1));

  // Grow with the text up to the CSS max-height.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [text]);

  // Starting a reply or an edit puts the caret at the end of the text.
  const draftKey = draft ? `${draft.kind}:${draft.message.id}:${draft.kind === "edit" ? draft.token : ""}` : null;
  useEffect(() => {
    const el = ref.current;
    if (!el || !draftKey) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [draftKey]);

  function trackCaret() {
    const el = ref.current;
    if (el) setCaret(el.selectionStart);
  }

  function change(value: string, at: number) {
    props.onValue({ text: value, picked });
    setCaret(at);
    setActive(0);
    if (!editing && props.typing && value.trim() !== "") store.typing(id);
  }

  function pick(person: ChatUser) {
    if (!query) return;
    const next = insertMention(text, query, caret, person);
    props.onValue({ text: next.text, picked: new Set(picked).add(person.id) });
    setCaret(next.caret);
    requestAnimationFrame(() => ref.current?.setSelectionRange(next.caret, next.caret));
  }

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!canSend || saving) return;
    const mentions = mentionedIds(check.body, participants, selfId, picked);
    if (draft?.kind === "edit") {
      const message = draft.message;
      const sameMentions =
        mentions.length === message.mentions.length && mentions.every((m) => message.mentions.includes(m));
      if (check.body === message.body && sameMentions) {
        props.onDraftDone();
        return;
      }
      const token = draft.token;
      setSaving(true);
      store
        .edit(id, message.id, check.body, mentions)
        .then(() => props.onEditSaved(token), props.onError)
        .finally(() => setSaving(false));
      return;
    }
    const replyToId = draft?.kind === "reply" ? draft.message.id : null;
    const media = attached.length > 0 ? props.tray?.tray.ready() : null;
    if (attached.length > 0 && !media) return;
    if (store.send(id, text, { replyToId, mentions, ...(media ? { attachments: media } : {}) })) {
      if (media) props.tray?.tray.clear();
      props.onSent();
      props.onDraftDone();
    }
  }

  function paste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = [...event.clipboardData.files];
    // Office puts a picture of copied text beside it; text pastes as text.
    if (files.length === 0 || editing || !props.tray || event.clipboardData.getData("text/plain").trim() !== "") return;
    event.preventDefault();
    props.tray.tray.add(files, props.tray.limits);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing) return;
    if (suggesting) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActive((activeIndex + step + candidates.length) % candidates.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const person = candidates[activeIndex];
        if (person) pick(person);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setDismissed(query?.start ?? null);
        return;
      }
    }
    if (event.key === "Escape" && draft && !saving) {
      event.preventDefault();
      props.onDraftDone();
      return;
    }
    // Enter sends, Shift+Enter starts a new line.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  const listId = "chat-mention-list";
  return (
    <div className="chat-compose">
      {draft && (
        <div className="chat-draft">
          <span className="chat-draft-text">
            <strong>
              {draft.kind === "edit"
                ? "Editing"
                : `Replying to ${draft.message.sender_id === selfId ? "yourself" : personName(users, draft.message.sender_id)}`}
            </strong>{" "}
            <span className="muted">
              {excerpt(draft.kind === "reply" ? summaryText(draft.message) : draft.message.body)}
            </span>
          </span>
          <button
            type="button"
            className="chat-icon-button"
            aria-label="Cancel"
            disabled={saving}
            onClick={props.onDraftDone}
          >
            ×
          </button>
        </div>
      )}
      {suggesting && (
        <ul className="chat-mentions" id={listId} role="listbox" aria-label="Mention someone">
          {candidates.map((person, i) => (
            <li
              key={person.id}
              id={`${listId}-${person.id}`}
              role="option"
              aria-selected={i === activeIndex}
              className={i === activeIndex ? "active" : undefined}
              onPointerDown={(event) => {
                event.preventDefault();
                pick(person);
              }}
            >
              <MemberAvatar
                userId={person.id}
                name={person.name}
                avatarUrl={person.avatar_small_url ?? null}
                isSelf={false}
              />
              {person.name}
            </li>
          ))}
        </ul>
      )}
      {!editing && props.tray && <TrayView {...props.tray} />}
      <VoiceNotice recorder={recorder} />
      <form className="chat-composer" onSubmit={submit}>
        {voiceBusy ? (
          <VoiceBar recorder={recorder} />
        ) : (
          <>
            {!editing && props.tray && (
              <AttachButton onFiles={(files) => props.tray?.tray.add(files, props.tray.limits)} disabled={saving} />
            )}
            {!editing && props.share && (
              <ShareLocationButton open={props.share.open} onToggle={props.share.toggle} disabled={saving} />
            )}
            <label className="visually-hidden" htmlFor="chat-composer-input">
              {editing ? "Edit message" : "Message"}
            </label>
            <textarea
              id="chat-composer-input"
              ref={ref}
              rows={1}
              value={text}
              placeholder={editing ? "Edit message" : attached.length > 0 ? "Add a caption" : "Message"}
              onChange={(event) => change(event.target.value, event.target.selectionStart)}
              onKeyDown={onKeyDown}
              onPaste={paste}
              onSelect={trackCaret}
              readOnly={saving}
              aria-describedby="chat-composer-count"
              aria-autocomplete="list"
              aria-controls={suggesting ? listId : undefined}
              aria-activedescendant={suggesting ? `${listId}-${candidates[activeIndex]?.id}` : undefined}
              autoFocus
            />
            <span id="chat-composer-count" className={`chat-count${left < 0 ? " error" : ""}`} aria-live="polite">
              {check.length >= BODY_COUNT_FROM && (left < 0 ? `${-left} too many` : `${left} left`)}
            </span>
          </>
        )}
        {showMic ? (
          <MicButton key="mic" recorder={recorder} disabled={saving} />
        ) : (
          <button key="send" type="submit" disabled={!canSend || saving}>
            {editing ? "Save" : "Send"}
          </button>
        )}
      </form>
    </div>
  );
}
